import { addDays, isWithinInterval } from 'date-fns';
import api from 'state/axios';
import { sortMultipleFields, splitDateRange } from 'state/helpers';


class RateLimiter {
    constructor({ interval = 1000, maxRequests = 1 }) {
        this.interval = interval;
        this.maxRequests = maxRequests;
        this.queue = [];
        this.active = 0;

        setInterval(() => {
            this.active = 0;
            this._drain();
        }, this.interval);
    }

    _drain() {
        while (this.active < this.maxRequests && this.queue.length > 0) {
            const resolve = this.queue.shift();
            this.active++;
            resolve();
        }
    }

    async acquire(signal) {
        if (signal?.aborted) {
            throw new DOMException("Aborted", "AbortError");
        }

        return new Promise((resolve, reject) => {
            if (signal) {
                signal.addEventListener("abort", () => {
                    reject(new DOMException("Aborted", "AbortError"));
                });
            }

            this.queue.push(resolve);
            this._drain();
        });
    }
}

const extractError = (err) => {
    const msg = err.response?.data?.message || err.message;
    const status = err.response?.status;
    return new Error(`${msg} (status: ${status})`);
};

/* API */
export const updatePreferences = async (userId, newPrefs) => {
    try {
        const response = await api.put(`/users/${userId}/preferences`, { preferences: newPrefs });
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchData = async (method, path, payload) => {
    try {
        const response = await api[method](path, payload);
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchParams = async () => {
    try {
        const response = await api.get('/params');
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchCrewRanks = async () => {
    try {
        const response = await api.get('/ranks/active');
        return Array.isArray(response.data)
            ? response.data.slice().sort((a, b) => (a.rankPriority ?? Infinity) - (b.rankPriority ?? Infinity))
            : [];
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchFlights = async (startDate, endDate, isLegMembers) => {
    if (!startDate || !endDate) return [];
    try {
        const response = await api.post('/flights', { startDate, endDate, isLegMembers });
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithRetry = async (fetchFn, { retries = 2, backoff = 500, signal } = {}) => {
    let attempt = 0;
    while (true) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        try {
            return await fetchFn();
        } catch (err) {
            attempt++;
            if (attempt > retries || signal?.aborted) throw err;
            await sleep(backoff * Math.pow(2, attempt - 1));
        }
    }
};

const rateLimiter = new RateLimiter({ interval: 1000, maxRequests: 2 });

const fetchWithRateLimit = async (fetchFn, { limiter, retries = 2, backoff = 500, signal, delayAfterSuccess = 1000 }) => {
    await limiter.acquire(signal);
    const result = await fetchWithRetry(fetchFn, { retries, backoff, signal });
    await sleep(delayAfterSuccess);
    return result;
};

export const chunkFlights = async (
    timeMode,
    startDate,
    endDate,
    LOAD_CHUNK_DAYS = 66,
    FETCH_TYPE = "SEQUENCE",
    options = {}
) => {
    const {
        concurrency = 2,
        retries = 2,
        backoff = 500,
        signal,
        onProgress,
        onChunk,
    } = options;

    const streaming = typeof onChunk === 'function';
    const SORT_FIELDS = [
        { field: 'flightDate', direction: 'asc' },
        { field: 'etd', direction: 'asc' },
    ];

    try {
        const ranges = splitDateRange(addDays(startDate, -1), addDays(endDate, 1), LOAD_CHUNK_DAYS);
        const total = ranges.length;
        let completed = 0;
        let allFlights = streaming ? null : [];
        let isFirst = true;

        const reportProgress = () => {
            if (onProgress) onProgress(Math.round((completed / total) * 100));
        };

        const handleChunk = (rawFlights) => {
            const filtered = rawFlights.filter(flight => {
                const date = timeMode === "UTC" ? flight.flightDate : flight.flightDateLocalBase;
                return isWithinInterval(date, { start: startDate, end: endDate });
            });
            if (!filtered.length) return;

            if (streaming) {
                const sorted = sortMultipleFields(filtered, SORT_FIELDS);
                onChunk(sorted, isFirst);
                isFirst = false;
            } else {
                allFlights = allFlights.concat(filtered);
            }
        };

        if (FETCH_TYPE === "SEQUENCE") {
            for (const range of ranges) {
                if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
                const flights = await fetchWithRateLimit(
                    () => fetchFlights(range.start, range.end),
                    { limiter: rateLimiter, retries, backoff, signal }
                );
                if (Array.isArray(flights) && flights.length) handleChunk(flights);
                completed++;
                reportProgress();
            }
        } else if (FETCH_TYPE === "BATCH") {
            let index = 0;
            const workers = Array.from({ length: concurrency }, async () => {
                while (index < ranges.length) {
                    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
                    const range = ranges[index++];
                    try {
                        const flights = await fetchWithRateLimit(
                            () => fetchFlights(range.start, range.end),
                            { limiter: rateLimiter, retries, backoff, signal }
                        );
                        if (Array.isArray(flights) && flights.length) handleChunk(flights);
                    } catch (err) {
                        console.error("Fetch flights failed:", range, err);
                    } finally {
                        completed++;
                        reportProgress();
                    }
                }
            });
            await Promise.all(workers);
        }

        if (streaming) return [];

        return sortMultipleFields(allFlights, SORT_FIELDS);
    } catch (error) {
        if (error.name === "AbortError") {
            console.warn("Chunk flights aborted!");
            throw error;
        }
        throw extractError(error);
    }
};

export const fetchCrewRoutes = async (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    try {
        const response = await api.post(
            '/crewroutes/legmembers', 
            { startDate, endDate }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchCrewBlockHours = async (startDate, endDate, crewCode) => {
    if (!startDate || !endDate) return [];
    try {
        const response = await api.post(
            '/legmembers/summarybycrew', 
            { startDate, endDate, crewCode }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchCrewTotalHours = async (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    try {
        const response = await api.post(
            '/rosterdetails/summarybycrew', 
            { startDate, endDate }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchLegMembersById = async (id) => {
    try {
        const response = await api.get(`/flights/legmembers/${id}`);
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchLegMembersByFlt = async (flightDate, flightDesc, dep) => {
    try {
        const response = await api.post(
            '/flights/legmembersbyflt', 
            { flightDate, flightDesc, dep }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchLegMembersByCrewCode = async (crewCode, startDate, endDate) => {
    if (!crewCode || !startDate || !endDate) return null;
    try {
        const response = await api.get(
            `/legmembers/legmembersbycrewcode/${crewCode}`, 
            { params: { startDate, endDate } }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchGroupedLegMembersByCrewCode = async (crewCode, startDate, endDate) => {
    if (!crewCode || !startDate || !endDate) return null;
    try {
        const response = await api.get(
            `/legmembers/groupedlegmembersbycrewcode/${crewCode}`, 
            { params: { startDate, endDate } }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchRosterDetailsByCrewCode = async (crewCode, startDate, endDate) => {
    try {
        const response = await api.get(
            `/rosterdetails/rosterdetailsbycrewcode/${crewCode}`, 
            { params: { startDate, endDate } }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchGroupedRosterDetailsByCrewCode = async (crewCode, startDate, endDate) => {
    try {
        const response = await api.get(
            `/rosterdetails/groupedrosterdetailsbycrewcode/${crewCode}`, 
            { params: { startDate, endDate } }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};

export const fetchJumpSeaters = async (startDate, endDate) => {
    try {
        const response = await api.post(
            '/jumpseaters', 
            { startDate, endDate }
        );
        return response.data;
    } catch (err) {
        throw extractError(err);
    }
};
